import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { GroupDto, UserPublic } from '@mondy/shared';
import { api } from '@/api';
import { ApiError } from '@mondy/shared/api-client';
import { setToken } from '@/lib/auth';
import { useRefreshCurrentUser } from '@/hooks/useCurrentUser';
import { useSetActiveGroup } from '@/state/activeGroup';
import { localTimezone } from '@/lib/format';
import { ButtonSpinner } from './FormField';
import { JoinedGroupModal } from '@/components/JoinedGroupModal';

interface GoogleCredentialResponse {
  credential: string;
}

interface GoogleAccountsId {
  initialize: (config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
  }) => void;
  renderButton: (
    element: HTMLElement,
    config: { type?: string; theme?: string; size?: string; width?: number },
  ) => void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: GoogleAccountsId;
      };
    };
  }
}

const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

function loadGsiScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GSI_SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Google script')), {
        once: true,
      });
      return;
    }
    const script = document.createElement('script');
    script.src = GSI_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google script'));
    document.head.appendChild(script);
  });
}

interface Props {
  clientId: string | null;
  // When present, resume navigation here after login. Today only
  // `/accept_group_invite/:token` is whitelisted upstream.
  next?: string | null;
  // When coming from an invite link, pass through so the server auto-joins
  // on first-ever Google login (Google vouches for the email).
  inviteToken?: string | null;
}

interface GoogleAuthResponse {
  user: UserPublic;
  token: string;
  // Populated by the server when a pending `inviteToken` resolved. We show
  // a welcome modal before navigating.
  joinedGroup: GroupDto | null;
  inviteError: string | null;
}

function humanizeInviteError(code: string): string {
  switch (code) {
    case 'EXPIRED':
      return 'The invitation has expired. Ask the group owner to send a new one.';
    case 'EMAIL_MISMATCH':
      return 'The invitation was sent to a different address than your Google account.';
    case 'INVALID':
      return 'The invitation is no longer valid.';
    default:
      return 'The invitation could not be applied.';
  }
}

export function GoogleSignInButton({ clientId, next, inviteToken }: Props) {
  const navigate = useNavigate();
  const refreshMe = useRefreshCurrentUser();
  const setActiveGroupId = useSetActiveGroup();
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [joinedGroup, setJoinedGroup] = useState<GroupDto | null>(null);
  const [inviteNotice, setInviteNotice] = useState<string | null>(null);
  const pendingNavigateTo = useRef<string>(next ?? '/');

  // Keep the latest `next` in a ref so the callback closure doesn't capture a
  // stale value between re-renders of the button.
  useEffect(() => {
    pendingNavigateTo.current = next ?? '/';
  }, [next]);

  const handleCredential = useCallback(
    async (response: GoogleCredentialResponse) => {
      setError(null);
      setInviteNotice(null);
      setPending(true);
      try {
        const res = await api.post<GoogleAuthResponse>('/api/auth/google', {
          idToken: response.credential,
          timezone: localTimezone(),
          ...(inviteToken ? { inviteToken } : {}),
        });
        setToken(res.token);
        await refreshMe();
        if (res.inviteError) {
          setInviteNotice(humanizeInviteError(res.inviteError));
        }
        if (res.joinedGroup) {
          setActiveGroupId(res.joinedGroup.id);
          setJoinedGroup(res.joinedGroup);
          // navigate after modal dismiss
          return;
        }
        navigate(pendingNavigateTo.current);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Google sign-in failed');
        setPending(false);
      }
    },
    [inviteToken, navigate, refreshMe, setActiveGroupId],
  );

  useEffect(() => {
    if (!clientId || !buttonRef.current) return;
    let cancelled = false;

    loadGsiScript()
      .then(() => {
        if (cancelled || !buttonRef.current) return;
        const google = window.google;
        if (!google?.accounts?.id) return;
        google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredential,
        });
        google.accounts.id.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'filled_black',
          size: 'large',
          width: 320,
        });
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load Google sign-in');
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, handleCredential]);

  if (!clientId) {
    return (
      <p className="text-center text-xs text-[color:var(--color-fg-muted)]">
        Google sign-in disabled — set <code>VITE_GOOGLE_CLIENT_ID</code> to enable
      </p>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative">
        <div
          ref={buttonRef}
          className={pending ? 'pointer-events-none opacity-60' : undefined}
        />
        {pending ? (
          <div
            className="absolute inset-0 flex items-center justify-center gap-2 rounded-md bg-[color:var(--color-bg)]/70 text-sm text-[color:var(--color-fg)]"
            role="status"
            aria-live="polite"
          >
            <ButtonSpinner />
            <span>Signing in…</span>
          </div>
        ) : null}
      </div>
      {error ? <p className="text-sm text-[color:var(--color-danger)]">{error}</p> : null}
      {inviteNotice ? (
        <p className="text-xs text-[color:var(--color-fg-muted)]">
          {inviteNotice}
        </p>
      ) : null}
      {joinedGroup ? (
        <JoinedGroupModal
          name={joinedGroup.name}
          onDismiss={() => {
            setJoinedGroup(null);
            navigate(pendingNavigateTo.current);
          }}
        />
      ) : null}
    </div>
  );
}
